/**
 * Cloud implementation of the shared HexAlertsApi (node-scoped) — ONE site's board.
 *   loadCameras(): the selected node's cameras, grouped by their group, with online state
 *                  derived from the camera's live status (streaming/recording/active).
 *   watch(): one-time snapshot (counters + recent feed + online flags) on mount/node-switch,
 *            then LIVE updates over the cloud BE's /ws/node-events socket — the cloud mirror
 *            of the local dashboard's socket.io alert stream (no polling). The local backend
 *            publishes each alert:new/alert:lifecycle to Redis → cloud-tunnel → cloud BE → here.
 *
 * The node-shape → HexAlert/HexCamera mapping lives in hexAlerts.core, shared with the
 * all-sites adapter (multiSiteHexAlerts.adapter) so the two boards can't drift apart on what
 * an alert or an online camera IS.
 */
import type { HexAlertsApi, HexAlertHandlers, HexAlert, HexAlertPage, HexCamera } from '@trinetra/shared';
import { selectedNodeId } from './node';
import {
  buildCounters,
  currentUser,
  getPreviewMeta,
  isActive,
  isOnline,
  mapAlert,
  rememberPreview,
  toHexCamera,
  toSeverity,
} from './hexAlerts.core';
import {
  getNodeCameras,
  getNodeRealtimeAlerts,
  getNodeAlerts,
  getNodeModelEventImage,
  acknowledgeNodeAlert,
  resolveNodeAlert,
  getStoredToken,
} from '../services/api.service';

const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;
// A link that stays open this long is considered healthy → reset the backoff.
const RECONNECT_STABLE_MS = 10000;

export const cloudHexAlertsApi: HexAlertsApi = {
  async loadCameras(): Promise<HexCamera[]> {
    const nodeId = selectedNodeId();
    if (!nodeId) return [];
    const raw: any = await getNodeCameras(nodeId);
    const cams: any[] = Array.isArray(raw) ? raw : raw?.data || raw?.cameras || [];
    // siteName stays null: this board shows one site and the switcher above it already
    // names that site (the shared page draws no site panels for it either).
    return cams.map((c) => toHexCamera(c, nodeId, null));
  },

  watch({ onSnapshot, onAlert, onAlertPreview, onLifecycle, onLiveStatus, onRecordingStatus }: HexAlertHandlers) {
    const nodeId = selectedNodeId();
    if (!nodeId) return () => {};
    let cancelled = false;

    // ── 1) One-time snapshot (NOT polling) ──────────────────────────────────
    // Paint the layout immediately on mount / node switch — counters + recent feed
    // from realtime-alerts (active only). Also re-run after a socket RE-connect to
    // resync any events missed during the gap. Online state is NOT seeded here:
    // loadCameras() already seeds it from cameras.status and the live camera:status_changed
    // socket below keeps it fresh after mount, so re-deriving it from realtime-stats (the
    // same status field, fetched separately) would only add a redundant, possibly-staler
    // second source that could flicker the hex colour.
    const loadSnapshot = async () => {
      try {
        // Filter to active alerts server-side (like the local node). Without status,
        // the backend returns the most-recent 100 of ALL statuses and the 'new' ones
        // get crowded out by resolved/acknowledged rows → empty feed → no counts/tiles.
        const alertsRaw = await getNodeRealtimeAlerts(nodeId, { status: 'new', limit: '200' }).catch(() => null);
        if (cancelled) return;

        const alertList: any[] = Array.isArray(alertsRaw)
          ? alertsRaw
          : (alertsRaw as any)?.alerts || (alertsRaw as any)?.data || [];
        const recent = alertList.filter((a) => isActive(a.status)).map(mapAlert);
        onSnapshot(buildCounters(recent), recent);
      } catch {
        /* ignore — the live socket below keeps the feed fresh */
      }
    };
    loadSnapshot();

    // ── 2) Live updates over /ws/node-events ────────────────────────────────
    // The cloud mirror of the local dashboard's socket.io alert stream: the local
    // backend publishes each alert:new/alert:lifecycle to Redis → cloud-tunnel pushes
    // it up the tunnel → cloud BE relays it here. alert:new merges in; alert:lifecycle
    // (ack/resolve/cancel) drops the alert off the layout.
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = getStoredToken();
    const url = `${proto}//${window.location.host}/ws/node-events?token=${encodeURIComponent(token || '')}&nodeId=${encodeURIComponent(nodeId)}`;

    let ws: WebSocket | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stableTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;

    const connect = () => {
      if (cancelled) return;
      ws = new WebSocket(url);

      ws.onopen = () => {
        // Only reset the backoff once the link has proven stable; a socket that
        // opens then immediately closes (flap) must keep backing off, otherwise it
        // reconnects — and refetches the snapshot below — every RECONNECT_BASE_MS.
        stableTimer = setTimeout(() => { reconnectAttempts = 0; }, RECONNECT_STABLE_MS);
        // (Re)load the snapshot every time the node-events link is established — INCLUDING the
        // first open. The mount-time loadSnapshot() above can race a cold/idle node tunnel (the
        // monitoring_proxy command times out, or the cloud BE's nodeManager.isOnline gate reads
        // false for an instant → 503) and then silently fails (its catch swallows the error), so
        // on a page reload the hex stays empty until a live alert:new arrives. The events socket
        // opening is the reliable signal the cloud↔node path is live, so refetch here. Idempotent:
        // onSnapshot replaces counters+feed. Mirrors the local node, whose snapshot hits the
        // command-center directly (no tunnel warmup) and so always repaints active alerts on reload.
        loadSnapshot();
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 15000);
      };

      ws.onmessage = (ev) => {
        let msg: any;
        try {
          msg = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        const d = msg?.data || {};
        if (msg.type === 'alert:new') {
          const a = mapAlert(d);
          if (isActive(a.status)) onAlert?.(a);
        } else if (msg.type === 'alert:preview') {
          // The node has committed the event row and its images, so the snapshot this alert
          // arrived without can now be resolved. Record how to fetch it, then nudge the page
          // so the card re-runs loadPreview. snapshotUrl stays null — cloud has no route to
          // the node's nginx and must blob-fetch through the tunnel.
          if (rememberPreview(d.alert_id, d.source_table, d.event_id, d.image_urls)) {
            onAlertPreview?.({ alertId: d.alert_id, cameraId: d.camera_id, snapshotUrl: null });
          }
        } else if (msg.type === 'alert:lifecycle') {
          onLifecycle?.({
            alertId: d.alert_id,
            cameraId: d.camera_id,
            status: d.status,
            severity: toSeverity(d.severity),
          });
        } else if (msg.type === 'camera:status_changed') {
          // The cloud only receives the overall camera:status_changed over the tunnel
          // (the node doesn't relay separate stream:*/recording:* up), so it drives both
          // the LIVE and REC dots together. Mirrors the local recording pipeline state.
          if (d.camera_id && d.status !== undefined) {
            const up = isOnline(d.status);
            onLiveStatus?.({ [d.camera_id]: up });
            onRecordingStatus?.({ [d.camera_id]: up });
          }
        }
      };

      ws.onclose = () => {
        if (pingTimer) {
          clearInterval(pingTimer);
          pingTimer = null;
        }
        if (stableTimer) {
          clearTimeout(stableTimer);
          stableTimer = null;
        }
        if (!cancelled) {
          // Exponential backoff with jitter, capped — so a node whose events
          // socket keeps flapping doesn't hammer the tunnel snapshot every 3s.
          const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS);
          reconnectAttempts++;
          const jitter = delay * (0.8 + Math.random() * 0.4);
          reconnectTimer = setTimeout(connect, jitter);
        }
      };
      ws.onerror = () => {
        try {
          ws?.close();
        } catch {
          /* ignore */
        }
      };
    };
    connect();

    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (stableTimer) clearTimeout(stableTimer);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    };
  },

  async listActiveAlerts({ page, limit }: { page: number; limit: number }): Promise<HexAlertPage> {
    const nodeId = selectedNodeId();
    if (!nodeId) return { data: [], page, totalPages: 1, total: 0 };
    const res: any = await getNodeAlerts(nodeId, {
      status: 'new',
      page,
      limit,
      sort_by: 'event_timestamp',
      sort_order: 'desc',
    }).catch(() => null);
    const rows: any[] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    const data = rows.filter((a) => isActive(a.status)).map(mapAlert);
    const p = res?.pagination || {};
    return {
      data,
      page: p.page || page,
      totalPages: p.total_pages || 1,
      total: p.total || data.length,
    };
  },

  /**
   * Lazily resolve a card's preview image (the alert's SAVED snapshot, from the DB) as a
   * blob URL fetched through the tunnel. Non-blocking by design: the card renders first and
   * calls this after, so the realtime feed is never held up by image loading. Returns null
   * when the alert has no captured snapshot meta (e.g. a slim socket alert not yet backed by
   * a DB row) or the fetch fails — the card then keeps its "No preview" placeholder.
   */
  async loadPreview(alert: HexAlert): Promise<string | null> {
    const nodeId = selectedNodeId();
    if (!nodeId || !alert.id) return null;
    const meta = getPreviewMeta(alert.id);
    if (!meta) return null;
    try {
      const blob: any = await getNodeModelEventImage(nodeId, meta.model, meta.eventId, meta.imageType);
      // A tunnel error (404/timeout) still resolves as a Blob (of JSON/text) under
      // responseType:'blob' — only turn real image bytes into an object URL.
      if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },

  async acknowledgeAlert(alertId: string) {
    const nodeId = selectedNodeId();
    if (!nodeId) return;
    const u = currentUser();
    await acknowledgeNodeAlert(nodeId, alertId, u.id, u.name);
  },

  async resolveAlert(alertId: string, note?: string) {
    const nodeId = selectedNodeId();
    if (!nodeId) return;
    const u = currentUser();
    await resolveNodeAlert(nodeId, alertId, u.id, note ?? '', u.name);
  },
};
